import React, { useEffect, useRef, useState } from "react";
import p5 from "p5";
import { Hands } from "@mediapipe/hands";
import { Camera } from "@mediapipe/camera_utils";
import { playCatchSound, playMissSound, playLevelUpSound } from "../utils/audio";
import "./GameCanvas.css";
import ReactGA from "react-ga4";

const GameCanvas = ({ onLevelChange, sensitivity = "Fast", setSensitivity }) => {
  const scoreRef = useRef(null);
  const missesRef = useRef(null);
  const comboRef = useRef(null);
  const pausedRef = useRef(null);
  const canvasParentRef = useRef(null);
  const p5InstanceRef = useRef(null);
  const sensitivityRef = useRef(sensitivity);

  useEffect(() => {
    sensitivityRef.current = sensitivity;
  }, [sensitivity]);

  const [gameOver, setGameOver] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [showRewardedAd, setShowRewardedAd] = useState(false);
  const [adPlaying, setAdPlaying] = useState(false);
  const [cameraError, setCameraError] = useState(false);

  useEffect(() => {
    if (gameOver) {
      ReactGA.event({ category: "Game", action: "Game Over", value: finalScore });
    }
  }, [gameOver, finalScore]);

  const hasUsedRevive = useRef(false);

  const gameState = useRef({
    score: 0,
    misses: 0,
    level: "Easy",
    stopped: false,
    paused: false, // Start active since App handles setup
    combo: 0,
    maxCombo: 0,
  });

  useEffect(() => {
    let balls = [];
    let particles = [];
    let floatingTexts = [];
    let video, hands, camera;
    let lastHandSeen = Date.now();
    let lastSpawnTime = 0;
    let screenShake = 0;
    let gridOffset = 0;

    const DIFFICULTY_SETTINGS = {
      Easy: { speed: 6, interval: 1400, color: '#10b981' },
      Medium: { speed: 9, interval: 900, color: '#3b82f6' },
      Hard: { speed: 13, interval: 550, color: '#ef4444' },
    };

    const BALL_TYPES = {
      NORMAL: { weight: 0.8, points: 10, size: 28, color: '#10b981' },
      FLASH: { weight: 0.1, points: 50, size: 22, trail: true, speedMultiplier: 1.7, color: '#3b82f6' },
      GOLDEN: { weight: 0.05, points: 150, size: 36, trail: true, color: '#f59e0b' },
      HEART: { weight: 0.05, points: 5, size: 32, type: 'HEART', color: '#ef4444' }
    };

    const sketch = (p) => {
      let catcherX;

      p.setup = () => {
        const container = p.select('#game-container');
        const containerW = container.width;
        const h = p.min(p.windowHeight * 0.85, 800);
        p.createCanvas(containerW, h).parent(container);
        catcherX = containerW / 2;
        
        try {
          video = p.createCapture(p.VIDEO);
          video.size(640, 480);
          video.hide();
          hands = new Hands({ locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}` });
          hands.setOptions({ 
            maxNumHands: 1, 
            modelComplexity: 0, 
            minDetectionConfidence: 0.4, 
            minTrackingConfidence: 0.4 
          });
          hands.onResults(gotHands);
          camera = new Camera(video.elt, {
            onFrame: async () => { if (video && video.elt) await hands.send({ image: video.elt }); },
            width: 640, height: 480,
          });
          camera.start().catch(() => setCameraError(true));
        } catch (err) { setCameraError(true); }
      };

      p.windowResized = () => {
        const container = p.select('#game-container');
        if (container) {
          const containerW = container.width;
          const h = p.min(p.windowHeight * 0.85, 800);
          p.resizeCanvas(containerW, h);
        }
      };

      p.draw = () => {
        p.push();
        if (screenShake > 0) p.translate(p.random(-screenShake, screenShake), p.random(-screenShake, screenShake));

        // --- BACKGROUND RENDER (CINEMATIC) ---
        p.background(5, 5, 10);
        
        // Dynamic Grid
        p.stroke(255, 255, 255, 15);
        p.strokeWeight(1);
        gridOffset = (gridOffset + 2) % 40;
        for (let x = 0; x <= p.width; x += 40) p.line(x, 0, x, p.height);
        for (let y = gridOffset; y <= p.height; y += 40) p.line(0, y, p.width, y);

        // Nebula Glows
        p.noStroke();
        for (let i = 0; i < 3; i++) {
          let alpha = p.map(p.sin(p.frameCount * 0.005 + i), -1, 1, 5, 25);
          if (i === 0) p.fill(16, 185, 129, alpha);
          else p.fill(59, 130, 246, alpha);
          p.ellipse(p.width * (0.2 + i * 0.3), p.height * 0.4, p.width * 0.6, p.height * 0.6);
        }

        if (gameState.current.stopped) { p.pop(); return; }

        // --- GAME LOGIC ---
        const settings = DIFFICULTY_SETTINGS[gameState.current.level];
        
        // Auto-pause check
        if (Date.now() - lastHandSeen > 1200) {
          gameState.current.paused = true;
          if (pausedRef.current) pausedRef.current.style.display = "flex";
        }

        // Spawn
        if (!gameState.current.paused && p.millis() - lastSpawnTime > settings.interval) {
          let typeRoll = p.random();
          let type = 'NORMAL';
          if (typeRoll > 0.95) type = 'GOLDEN';
          else if (typeRoll > 0.90) type = 'HEART';
          else if (typeRoll > 0.80) type = 'FLASH';

          const def = BALL_TYPES[type];
          // CEO FIX: Speed must be relative to height to feel the same on iPad vs Laptop
          const relativeSpeed = (p.height / 600) * settings.speed * (def.speedMultiplier || 1);
          
          balls.push({
            x: p.random(100, p.width - 100),
            y: -50,
            type: type,
            size: def.size,
            trail: def.trail,
            points: def.points,
            color: def.color,
            velocity: relativeSpeed,
            history: []
          });
          lastSpawnTime = p.millis();
        }

        // --- RENDER HUD (TACTICAL) ---
        renderHUD(p);

        // --- RENDER CATCHER (ENERGY SHIELD) ---
        const baseW = p.width < 600 ? 100 : 140;
        const catcherW = baseW + (gameState.current.combo * 2);
        p.push();
        // QA FIX: Moved to -50 to prevent desktop clipping
        p.translate(catcherX, p.height - 55);
        // Outer Glow
        p.noFill();
        p.stroke(16, 185, 129, 40 + p.sin(p.frameCount * 0.1) * 20);
        p.strokeWeight(4);
        p.rect(-catcherW/2 - 5, 0, catcherW + 10, 20, 8);
        // Main Shield
        p.fill(16, 185, 129, 20);
        p.stroke(16, 185, 129, 200);
        p.strokeWeight(2);
        p.rect(-catcherW/2, 0, catcherW, 20, 6);
        // Energy Core
        p.fill(255, 255, 255, 150);
        p.noStroke();
        p.rect(-catcherW/2 + 10, 8, catcherW - 20, 4, 2);
        p.pop();

        // --- RENDER BALLS (ORBS) ---
        for (let i = balls.length - 1; i >= 0; i--) {
          const b = balls[i];
          if (!gameState.current.paused) b.y += b.velocity;

          // Trail
          if (b.trail) {
            b.history.push({x: b.x, y: b.y});
            if (b.history.length > 8) b.history.shift();
            for (let j = 0; j < b.history.length; j++) {
              let a = p.map(j, 0, b.history.length, 0, 100);
              let c = p.color(b.color);
              c.setAlpha(a);
              p.fill(c);
              p.ellipse(b.history[j].x, b.history[j].y, b.size * (j/b.history.length));
            }
          }

          // Main Orb
          p.push();
          p.translate(b.x, b.y);
          let orbColor = p.color(b.color);
          orbColor.setAlpha(180);
          p.fill(orbColor);
          p.stroke(255, 255, 255, 100);
          p.strokeWeight(1);
          p.ellipse(0, 0, b.size, b.size);
          // Pulse
          let pulse = p.sin(p.frameCount * 0.2) * 4;
          p.noFill();
          p.stroke(b.color);
          p.ellipse(0, 0, b.size + pulse, b.size + pulse);
          p.pop();

          // Collision
          if (b.y > p.height - 80 && b.y < p.height - 40 && b.x > catcherX - catcherW/2 - 15 && b.x < catcherX + catcherW/2 + 15) {
            handleCatch(b, i, p);
          } else if (b.y > p.height + 50) {
            handleMiss(b, i);
          }
        }

        // Particles & Floating Text (Standard Logic)
        updateVFX(p, particles, floatingTexts);
        
        screenShake *= 0.85;
        p.pop();
      };

      const renderHUD = (p) => {
        // We use refs for actual data but draw visual "frames" in p5
        p.push();
        // Score Frame
        p.fill(22, 22, 26, 200);
        p.stroke(varToRGB('--glass-border'));
        p.rect(30, 30, 180, 50, 12);
        p.stroke(16, 185, 129, 100);
        p.line(30, 30, 60, 30);
        p.line(30, 30, 30, 50);
        
        // Misses Frame
        p.fill(22, 22, 26, 200);
        p.stroke(varToRGB('--glass-border'));
        p.rect(p.width - 210, 30, 180, 50, 12);
        p.stroke(239, 68, 68, 100);
        p.line(p.width - 30, 30, p.width - 60, 30);
        p.line(p.width - 30, 30, p.width - 30, 50);
        p.pop();
      };

      const handleCatch = (b, idx, p) => {
        gameState.current.combo++;
        setCombo(gameState.current.combo);
        let gain = b.points * (1 + Math.floor(gameState.current.combo / 10) * 0.5);
        gameState.current.score += Math.round(gain);
        if (b.type === 'HEART') gameState.current.misses = Math.max(0, gameState.current.misses - 1);
        playCatchSound(1 + gameState.current.combo * 0.03);
        
        if (scoreRef.current) scoreRef.current.innerText = gameState.current.score;
        if (missesRef.current) missesRef.current.innerText = `${gameState.current.misses}/10`;
        
        floatingTexts.push({ x: b.x, y: b.y, text: `+${Math.round(gain)}`, color: b.color, life: 50 });
        for (let j = 0; j < 15; j++) {
          particles.push({ x: b.x, y: b.y, vx: p.random(-4, 4), vy: p.random(-5, -2), a: 255, c: b.color, s: p.random(2, 6) });
        }
        balls.splice(idx, 1);

        // Level Scaling
        if (gameState.current.score >= 1200 && gameState.current.level !== 'Hard') {
          gameState.current.level = 'Hard'; onLevelChange('Hard'); playLevelUpSound();
        } else if (gameState.current.score >= 400 && gameState.current.level === 'Easy') {
          gameState.current.level = 'Medium'; onLevelChange('Medium'); playLevelUpSound();
        }
      };

      const handleMiss = (b, idx) => {
        if (b.type !== 'HEART') {
          gameState.current.misses++;
          gameState.current.combo = 0;
          setCombo(0);
          screenShake = 20;
          playMissSound();
          if (missesRef.current) missesRef.current.innerText = `${gameState.current.misses}/10`;
          if (gameState.current.misses >= 10) handleGameOver();
        }
        balls.splice(idx, 1);
      };

      const handleGameOver = () => {
        gameState.current.stopped = true;
        if (!hasUsedRevive.current) setShowRewardedAd(true);
        else {
          const best = localStorage.getItem('handStreamHighScore') || 0;
          if (gameState.current.score > best) localStorage.setItem('handStreamHighScore', gameState.current.score);
          setFinalScore(gameState.current.score);
          setGameOver(true);
        }
      };

      const gotHands = (results) => {
        if (gameState.current.stopped) return;
        if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
          const hand = results.multiHandLandmarks[0];
          const rawX = 1 - hand[8].x;
          let minR = 0.3, maxR = 0.7;
          if (sensitivityRef.current === 'Slow') { minR = 0.1; maxR = 0.9; }
          else if (sensitivityRef.current === 'Medium') { minR = 0.2; maxR = 0.8; }
          const target = p.map(rawX, minR, maxR, 0, p.width, true);
          catcherX += (target - catcherX) * 0.65; // Increased responsiveness
          lastHandSeen = Date.now();
          if (gameState.current.paused) {
            gameState.current.paused = false;
            if (pausedRef.current) pausedRef.current.style.display = "none";
          }
        }
      };
    };

    p5InstanceRef.current = new p5(sketch, canvasParentRef.current);
    return () => { if (p5InstanceRef.current) p5InstanceRef.current.remove(); };
  }, [onLevelChange]);

  const updateVFX = (p, particles, texts) => {
    for (let i = particles.length - 1; i >= 0; i--) {
      const par = particles[i];
      p.noStroke(); 
      let c = p.color(par.c);
      c.setAlpha(par.a);
      p.fill(c);
      p.ellipse(par.x, par.y, par.s);
      par.x += par.vx; par.y += par.vy; par.vy += 0.15; par.a -= 6;
      if (par.a <= 0) particles.splice(i, 1);
    }
    for (let i = texts.length - 1; i >= 0; i--) {
      const ft = texts[i];
      p.textAlign(p.CENTER); p.textSize(18); p.textStyle(p.BOLD); p.fill(ft.color);
      p.text(ft.text, ft.x, ft.y);
      ft.y -= 1.2; ft.life--;
      if (ft.life <= 0) texts.splice(i, 1);
    }
  };

  const varToRGB = (name) => {
    // Helper to get color from CSS variables for p5
    return p5InstanceRef.current ? p5InstanceRef.current.color(getComputedStyle(document.documentElement).getPropertyValue(name).trim() || '#fff') : '#fff';
  };

  const restartGame = () => { window.location.reload(); };
  const watchAd = () => {
    setAdPlaying(true);
    setTimeout(() => {
      setAdPlaying(false); setShowRewardedAd(false); hasUsedRevive.current = true;
      gameState.current.misses = 5; if (missesRef.current) missesRef.current.innerText = "5/10";
      gameState.current.stopped = false; gameState.current.paused = true;
    }, 2500);
  };

  return (
    <div id="game-container">
      <div ref={canvasParentRef}></div>
      
      {/* TACTICAL HUD OVERLAY */}
      <div className="hud-readout score-readout">
        <span className="label">SCORE</span>
        <div className="value" ref={scoreRef}>0</div>
      </div>
      
      <div className="hud-readout miss-readout">
        <span className="label">STRIKES</span>
        <div className="value" ref={missesRef}>0/10</div>
      </div>
      
      <div className={`combo-badge elite ${combo > 0 ? 'show' : ''}`} ref={comboRef}>
        <div className="combo-inner">
          <span className="mult">{combo}X</span>
          <span className="tag">COMBO</span>
        </div>
      </div>

      <div className="paused-overlay" ref={pausedRef}>
        <div className="pause-content">
          <div className="pulse-icon">📡</div>
          <h2>SENSOR LINK LOST</h2>
          <p>Place your hand in the frame to resume</p>
        </div>
      </div>

      {cameraError && <div className="error-overlay"><h3>HARDWARE LINK FAILED</h3><p>Verify camera permissions and refresh</p></div>}

      {showRewardedAd && (
        <div className="overlay ad-overlay">
          <div className="reward-box">
            {adPlaying ? (
              <div className="loading"><h3>RECALIBRATING...</h3><div className="bar"></div></div>
            ) : (
              <>
                <h2 className="critical">CRITICAL DAMAGE</h2>
                <p>Execute emergency repairs to continue?</p>
                <button className="btn-reward" onClick={watchAd}>WATCH AD (+5 LIVES)</button>
                <button className="btn-skip" onClick={() => { setShowRewardedAd(false); setGameOver(true); setFinalScore(gameState.current.score); }}>ABANDON MISSION</button>
              </>
            )}
          </div>
        </div>
      )}

      {gameOver && (
        <div className="overlay finish-overlay">
          <div className="final-modal">
            <h1 className="glitch" data-text="MISSION OVER">MISSION OVER</h1>
            <div className="stats-grid">
              <div className="stat-card"><span>SCORE</span><div className="val">{finalScore}</div></div>
              <div className="stat-card"><span>BEST</span><div className="val">{localStorage.getItem('handStreamHighScore') || 0}</div></div>
            </div>
            <button className="btn-restart" onClick={restartGame}>RE-ENGAGE</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameCanvas;
